import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertTriangle,
  Clock,
  FileText,
  Info,
  Trash2,
  Upload,
} from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DateInputWithCalendar } from '@/components/ui/date-input-with-calendar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { handleGlobalError } from '@/utils/error-handler';
import {
  cancelDebtPayment,
  submitDebtPayment,
} from '@/api/manager-balance/debtPaymentsApi';
import {
  ACCEPTED_RECEIPT_TYPES,
  MAX_NOTES_LENGTH,
  MAX_TRANSFER_AGE_DAYS,
  makeDebtPaymentSchema,
  type DebtPaymentFormValues,
} from '@/schemas/debtPaymentSchema';
import type { DebtPaymentRequest } from '@/types/manager.model';
import {
  currencyFormatter,
  formatMovementDate,
  formatTransferDate,
} from '@/utils/movements-utils';

export type DrawerMode = 'create' | 'detail';

interface CancelDebtDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: DrawerMode;
  request?: DebtPaymentRequest | null;
  negativeBalance: number;
  onChanged: () => void;
}

/**
 * Combines the selected date with the current time and serializes it as a LocalDateTime ISO string.
 * This keeps a consistent date+time format across all movement history entries.
 */
const toLocalDateTimeNow = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const now = new Date();
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

/** Groups thousands with dots: "1234567" → "1.234.567". */
const groupThousands = (digits: string): string =>
  digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/** number → AR-locale string (thousands with `.`, decimals with `,`), e.g. 1234.5 → "1.234,5". */
const formatAmount = (value: number): string =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <label className="mb-1.5 block text-[13px] font-semibold text-[#374151]">
    {children}
    {required && <span className="ml-0.5 text-[#DC2626]">*</span>}
  </label>
);

/**
 * Amount input in AR locale format: thousands with `.`, decimals with `,`, no spinners.
 * Keeps the typed text as the source of truth and emits the parsed number.
 */
const AmountInput: React.FC<{
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}> = ({ value, onChange }) => {
  const [text, setText] = useState('');

  // Syncs from the outside (reset on open, prefill on resend) without overwriting what the user is typing.
  useEffect(() => {
    const incoming =
      value == null || Number.isNaN(value) ? undefined : value;
    setText((current) => {
      const currentNum = parseAmountText(current);
      if (currentNum === incoming) return current;
      return incoming == null ? '' : formatAmount(incoming);
    });
  }, [value]);

  const handleChange = (raw: string) => {
    let sanitized = raw.replace(/[^\d,]/g, '');
    const firstComma = sanitized.indexOf(',');
    if (firstComma !== -1) {
      sanitized =
        sanitized.slice(0, firstComma + 1) +
        sanitized.slice(firstComma + 1).replace(/,/g, '');
    }

    const hasComma = sanitized.includes(',');
    const [intRaw = '', decRaw = ''] = sanitized.split(',');
    const intDigits = intRaw.replace(/^0+(?=\d)/, '');
    const decDigits = decRaw.slice(0, 2);

    const display = hasComma
      ? `${groupThousands(intDigits)},${decDigits}`
      : groupThousands(intDigits);
    setText(display);

    if (sanitized === '' || sanitized === ',') {
      onChange(undefined);
      return;
    }
    const num = Number(`${intDigits || '0'}.${hasComma ? decDigits || '0' : '0'}`);
    onChange(Number.isNaN(num) ? undefined : num);
  };

  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#6B7280]">
        $
      </span>
      <Input
        type="text"
        inputMode="decimal"
        className="pl-7"
        placeholder="0,00"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        autoFocus
      />
    </div>
  );
};

/** AR-locale string ("1.234,56") → number. Returns undefined if empty. */
const parseAmountText = (text: string): number | undefined => {
  const sanitized = text.replace(/\./g, '').replace(',', '.');
  if (sanitized.trim() === '') return undefined;
  const num = Number(sanitized);
  return Number.isNaN(num) ? undefined : num;
};

const ReceiptUploader: React.FC<{
  file: File | null;
  onSelect: (file: File | null) => void;
  error?: string;
}> = ({ file, onSelect, error }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (files: FileList | null) => {
    const next = files?.[0];
    if (next) onSelect(next);
  };

  if (file) {
    const isPdf = file.type === 'application/pdf';
    return (
      <div className="flex items-center gap-3 rounded-[10px] border border-[#E7E9EE] bg-white p-3">
        <div
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-lg',
            isPdf ? 'bg-[#FCEAEA] text-[#DC2626]' : 'bg-[#E9EFFE] text-[#2E66E5]',
          )}
        >
          <FileText className="h-6 w-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[#1F2937]">
            {file.name}
          </p>
          <p className="text-xs text-[#6B7280]">
            {formatFileSize(file.size)} · subido recién
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={() => onSelect(null)}
          aria-label="Eliminar comprobante"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex w-full flex-col items-center gap-2 rounded-[10px] border border-dashed bg-[#FAFBFC] px-4 py-6 text-center transition-colors',
          dragging ? 'border-[#2E66E5] bg-[#E9EFFE]' : 'border-[#E7E9EE]',
          error && 'border-[#DC2626]',
        )}
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E9EFFE] text-[#2E66E5]">
          <Upload className="h-5 w-5" />
        </span>
        <span className="text-sm text-[#374151]">
          Arrastrá tu comprobante o{' '}
          <span className="font-semibold text-[#2E66E5] underline">
            elegí un archivo
          </span>
        </span>
        <span className="text-xs text-[#6B7280]">JPG, PNG o PDF · hasta 8 MB</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_RECEIPT_TYPES.join(',')}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {error && <p className="mt-1 text-xs text-[#DC2626]">{error}</p>}
    </div>
  );
};

const DETAIL_MESSAGES: Record<
  DebtPaymentRequest['status'],
  { className: string; title: string }
> = {
  PENDING_REVIEW: {
    className: 'bg-[#E5EEFF] text-[#1E40AF] border-[#B8CDFB]',
    title: 'Esperando validación del equipo de GetMatch. Te avisamos por mail.',
  },
  APPROVED: {
    className: 'bg-[#E6F6EC] text-[#0F7A35] border-[#A5D8B6]',
    title: 'Transferencia aprobada.',
  },
  REJECTED: {
    className: 'bg-[#FCEAEA] text-[#9F1B1B] border-[#F4B3B3]',
    title: 'Transferencia rechazada.',
  },
  CANCELLED: {
    className: 'bg-[#EEF0F4] text-[#4B5563] border-[#D5D9E0]',
    title: 'Solicitud cancelada por vos.',
  },
};

const CancelDebtDrawer: React.FC<CancelDebtDrawerProps> = ({
  open,
  onOpenChange,
  mode,
  request,
  negativeBalance,
  onChanged,
}) => {
  const { toast } = useToast();
  // In detail mode the user can switch to "create" via "Reenviar con corrección".
  const [viewMode, setViewMode] = useState<DrawerMode>(mode);
  const [isCancelling, setIsCancelling] = useState(false);

  const schema = useMemo(
    () => makeDebtPaymentSchema(negativeBalance),
    [negativeBalance],
  );

  // Calendar bounds: between 90 days ago and today.
  const { minTransferDate, maxTransferDate } = useMemo(() => {
    const max = new Date();
    const min = new Date();
    min.setDate(min.getDate() - MAX_TRANSFER_AGE_DAYS);
    return { minTransferDate: min, maxTransferDate: max };
  }, []);

  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<DebtPaymentFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { amount: undefined, transferDate: undefined, notes: '' },
  });

  // Reset al abrir / cambiar de solicitud.
  useEffect(() => {
    if (!open) return;
    setViewMode(mode);
    reset({ amount: undefined, transferDate: undefined, notes: '' });
    setIsCancelling(false);
  }, [open, mode, request?.id, reset]);

  const notes = watch('notes') ?? '';
  const receipt = watch('receipt') as File | undefined;

  const onSubmit = useCallback(
    async (values: DebtPaymentFormValues) => {
      try {
        await submitDebtPayment({
          amount: values.amount,
          transferDate: toLocalDateTimeNow(values.transferDate),
          receipt: values.receipt,
          notes: values.notes,
        });
        toast({
          title: 'Solicitud enviada',
          description: 'La transferencia quedó en revisión.',
        });
        onChanged();
        onOpenChange(false);
      } catch (err) {
        handleGlobalError(
          err,
          'No se pudo enviar la solicitud',
          'Revisá los datos e intentá de nuevo.',
        );
      }
    },
    [onChanged, onOpenChange, toast],
  );

  const onCancelRequest = useCallback(async () => {
    if (!request) return;
    setIsCancelling(true);
    try {
      await cancelDebtPayment(request.id);
      toast({
        title: 'Solicitud cancelada',
        description: 'Podés volver a enviar una nueva cuando quieras.',
      });
      onChanged();
      onOpenChange(false);
    } catch (err) {
      handleGlobalError(
        err,
        'No se pudo cancelar',
        'Intentá de nuevo en unos segundos.',
      );
    } finally {
      setIsCancelling(false);
    }
  }, [request, onChanged, onOpenChange, toast]);

  const startResend = useCallback(() => {
    if (request) {
      reset({
        amount: Number(request.amount),
        transferDate: undefined,
        notes: request.notes ?? '',
      });
    }
    setViewMode('create');
  }, [request, reset]);

  const isDetail = viewMode === 'detail' && !!request;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="border-b border-[#EEF0F4] px-[22px] pb-4 pt-5 pr-12">
          <h2 className="text-lg font-bold tracking-[-0.3px] text-[#1A2466]">
            Cancelar deuda por transferencia
          </h2>
          <p className="text-[13.5px] text-[#6B7280]">
            {isDetail
              ? 'Detalle de tu solicitud'
              : 'Validamos en hasta 24 hs hábiles'}
          </p>
        </div>

        {isDetail ? (
          <DetailBody request={request} />
        ) : (
          <form
            id="debt-payment-form"
            onSubmit={handleSubmit(onSubmit)}
            className="flex-1 space-y-4 overflow-y-auto px-[22px] py-5"
          >
            {/* Info banner */}
            <div className="flex gap-2 rounded-[10px] border border-[#2B57C2]/[0.13] bg-[#E9EFFE] p-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#2B57C2]" />
              <div className="text-[13px] text-[#374151]">
                <p className="font-semibold">
                  Tu deuda se descontará cuando aprobemos la transferencia.
                </p>
                <p className="mt-0.5 text-[#6B7280]">
                  Mientras esté en revisión podés cancelar tu solicitud. La
                  aprobación suele tardar hasta 24 hs hábiles.
                </p>
              </div>
            </div>

            {/* Contexto: saldo negativo actual */}
            <div className="rounded-[10px] border border-[#E7E9EE] bg-[#FAFBFC] px-3.5 py-3">
              <p className="text-xs text-[#6B7280]">Saldo negativo actual</p>
              <p className="text-xl font-bold text-[#DC2626]">
                {currencyFormatter.format(negativeBalance)}
              </p>
            </div>

            {/* Monto + Fecha */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel required>Monto transferido</FieldLabel>
                <Controller
                  control={control}
                  name="amount"
                  render={({ field }) => (
                    <AmountInput
                      value={field.value as number | undefined}
                      onChange={field.onChange}
                    />
                  )}
                />
                <p className="mt-1 text-xs text-[#6B7280]">
                  No tiene que coincidir con el saldo negativo total.
                </p>
                {errors.amount && (
                  <p className="mt-1 text-xs text-[#DC2626]">
                    {errors.amount.message}
                  </p>
                )}
              </div>
              <div>
                <FieldLabel required>Fecha de la transferencia</FieldLabel>
                <Controller
                  control={control}
                  name="transferDate"
                  render={({ field }) => (
                    <DateInputWithCalendar
                      value={(field.value as Date) ?? null}
                      onChange={(date) =>
                        field.onChange(date ?? (undefined as unknown as Date))
                      }
                      minDate={minTransferDate}
                      maxDate={maxTransferDate}
                    />
                  )}
                />
                <p className="mt-1 text-xs text-[#6B7280]">
                  La que figura en tu comprobante.
                </p>
                {errors.transferDate && (
                  <p className="mt-1 text-xs text-[#DC2626]">
                    {errors.transferDate.message}
                  </p>
                )}
              </div>
            </div>

            {/* Comprobante */}
            <div>
              <FieldLabel required>Comprobante</FieldLabel>
              <Controller
                control={control}
                name="receipt"
                render={({ field }) => (
                  <ReceiptUploader
                    file={(field.value as File) ?? null}
                    onSelect={(f) =>
                      field.onChange(f ?? (undefined as unknown as File))
                    }
                    error={errors.receipt?.message as string | undefined}
                  />
                )}
              />
            </div>

            {/* Comentario */}
            <div>
              <FieldLabel>Comentario (opcional)</FieldLabel>
              <Textarea
                rows={3}
                maxLength={MAX_NOTES_LENGTH}
                placeholder="Agregá cualquier detalle que nos ayude a validar."
                {...register('notes')}
              />
              <p className="mt-1 text-right text-xs text-[#6B7280]">
                {notes.length}/{MAX_NOTES_LENGTH}
              </p>
            </div>
          </form>
        )}

        {/* Footer */}
        <div className="flex flex-col-reverse gap-2.5 border-t border-[#EEF0F4] bg-[#FAFBFC] px-[22px] py-4 sm:flex-row sm:justify-end">
          {isDetail ? (
            <DetailFooter
              request={request}
              isCancelling={isCancelling}
              onClose={() => onOpenChange(false)}
              onCancelRequest={onCancelRequest}
              onResend={startResend}
            />
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Descartar
              </Button>
              <Button
                type="submit"
                form="debt-payment-form"
                disabled={isSubmitting || !receipt}
              >
                {isSubmitting ? 'Enviando…' : 'Enviar'}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const DetailBody: React.FC<{ request: DebtPaymentRequest }> = ({ request }) => {
  const meta = DETAIL_MESSAGES[request.status];
  let message = meta.title;
  if (request.status === 'APPROVED') {
    message = `Aprobada el ${formatMovementDate(
      request.reviewedAt,
    )} — descontamos ${currencyFormatter.format(
      request.amount,
    )} del saldo negativo.`;
  } else if (request.status === 'CANCELLED') {
    message = `Solicitud cancelada por vos el ${formatMovementDate(
      request.reviewedAt ?? request.createdAt,
    )}.`;
  }

  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-[22px] py-5">
      <div className={cn('rounded-[10px] border p-3 text-[13px]', meta.className)}>
        <p className="flex items-center gap-2 font-semibold">
          <Clock className="h-4 w-4" />
          {message}
        </p>
        {request.status === 'REJECTED' && request.reviewNotes && (
          <p className="mt-1 flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Motivo: {request.reviewNotes}</span>
          </p>
        )}
      </div>

      <ReadOnlyField label="Monto transferido">
        {currencyFormatter.format(request.amount)}
      </ReadOnlyField>
      <ReadOnlyField label="Fecha de la transferencia">
        {formatTransferDate(request.transferDate)}
      </ReadOnlyField>
      {request.notes && (
        <ReadOnlyField label="Comentario">{request.notes}</ReadOnlyField>
      )}
      {request.receiptUrl && (
        <a
          href={request.receiptUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#2E66E5] underline"
        >
          <FileText className="h-4 w-4" />
          Ver comprobante
        </a>
      )}
    </div>
  );
};

const ReadOnlyField: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <p className="mb-1.5 text-[13px] font-semibold text-[#374151]">{label}</p>
    <div className="rounded-lg border border-[#E7E9EE] bg-[#F1F2F5] px-3 py-2 text-sm text-[#1F2937]">
      {children}
    </div>
  </div>
);

const DetailFooter: React.FC<{
  request: DebtPaymentRequest;
  isCancelling: boolean;
  onClose: () => void;
  onCancelRequest: () => void;
  onResend: () => void;
}> = ({ request, isCancelling, onClose, onCancelRequest, onResend }) => (
  <>
    <Button type="button" variant="outline" onClick={onClose}>
      Cerrar
    </Button>
    {request.status === 'PENDING_REVIEW' && (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button type="button" variant="destructive" disabled={isCancelling}>
            {isCancelling ? 'Cancelando…' : 'Cancelar solicitud'}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar esta solicitud?</AlertDialogTitle>
            <AlertDialogDescription>
              La solicitud quedará cancelada y no se descontará nada del saldo
              negativo. Podés volver a enviar una nueva cuando quieras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Volver</AlertDialogCancel>
            <AlertDialogAction onClick={onCancelRequest}>
              Cancelar solicitud
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    )}
    {request.status === 'REJECTED' && (
      <Button type="button" onClick={onResend}>
        Reenviar con corrección
      </Button>
    )}
  </>
);

export default CancelDebtDrawer;
